import React from 'react';
import { MapPin, Clock, Building2, MoreVertical, Eye, Users, MessageSquare, XCircle, CheckCircle, Image as ImageIcon, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { timeAgo } from '../../../utils/timeAgo';
import { usePanelByOpportunityId } from '../../panels/hooks/use-panels';

const TYPE_COLORS = {
  'Full Time':  'bg-green-50 text-green-700 border border-green-200',
  'Part Time':  'bg-blue-50 text-blue-700 border border-blue-200',
  'Internship': 'bg-purple-50 text-purple-700 border border-purple-200',
  'Contract':   'bg-orange-50 text-orange-700 border border-orange-200',
};

const MyPostingCard = ({ opportunity, onViewApplicants, onEdit, onClose, onDelete }) => {
  const navigate = useNavigate();
  const {
    id,
    title,
    clinicName,
    location,
    type,
    experienceRequired,
    screeningQuestion,
    caseAttachmentUrl,
    createdAt,
    isOpen,
    views,
    _count,
  } = opportunity;

  const applicants = _count?.applications || 0;
  const { data: panelData } = usePanelByOpportunityId(id);
  const panel = panelData; // interceptor already unwrapped

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const MAX_LENGTH = 200;
  const isLongQuestion = screeningQuestion?.length > MAX_LENGTH;
  const displayQuestion = isExpanded || !isLongQuestion 
    ? screeningQuestion 
    : `${screeningQuestion.slice(0, MAX_LENGTH)}...`;

  return (
    <div className={`bg-white rounded-xl border p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden ${!isOpen ? 'border-red-200 opacity-80' : 'border-gray-200'}`}>
      {/* Header Row */}
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <h3 className="text-xl font-bold text-gray-800 break-words [overflow-wrap:anywhere]">{title}</h3>
            {/* Open / Closed status badge */}
            {isOpen ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                <CheckCircle size={11} /> Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                <XCircle size={11} /> Closed
              </span>
            )}
          </div>
        </div>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
          >
            <MoreVertical size={20} />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
                <button
                  onClick={() => { setMenuOpen(false); onEdit && onEdit(opportunity); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Edit
                </button>
                {isOpen && (
                  <button
                    onClick={() => { setMenuOpen(false); onClose && onClose(id); }}
                    className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                  >
                    Close Position
                  </button>
                )}
                <button
                  onClick={() => { setMenuOpen(false); onDelete && onDelete(id); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Meta Row */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
        <span className="flex items-center gap-1.5 font-medium break-words [overflow-wrap:anywhere]">
          <Building2 size={16} className="text-gray-400 shrink-0" /> {clinicName}
        </span>
        <span className="flex items-center gap-1.5 font-medium break-words [overflow-wrap:anywhere]">
          <MapPin size={16} className="text-gray-400 shrink-0" /> {location}
        </span>
        <span className="flex items-center gap-1.5 font-medium">
          <Clock size={16} className="text-gray-400 shrink-0" /> Posted {timeAgo(createdAt)}
        </span>
      </div>

      {/* Badges Row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[type] || 'bg-blue-50 text-blue-600'}`}>
          {type}
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 break-words [overflow-wrap:anywhere]">
          {experienceRequired}
        </span>
        {/* Panel badge */}
        {panel && (
          <button
            onClick={() => navigate(`/panels/${panel.id}`)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors"
          >
            <MessageSquare size={11} /> Discussion Panel ({panel._count?.members || 0} members)
          </button>
        )}
      </div>

      {/* Screening Question Box */}
      {screeningQuestion && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="text-xs text-gray-500 mb-1">Clinical Screening Question:</div>
          <p 
            className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap break-words overflow-hidden"
            onClick={() => isLongQuestion && setIsExpanded(!isExpanded)}
            style={{ cursor: isLongQuestion ? 'pointer' : 'default', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          >
            {displayQuestion}
          </p>
          {isLongQuestion && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-indigo-600 font-semibold text-xs mt-2 hover:underline focus:outline-none"
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}

          {/* Attached Clinical Case Image / Document */}
          {caseAttachmentUrl && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1.5">
                <ImageIcon size={14} /> Attached Clinical Case
              </div>
              {/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(caseAttachmentUrl) ? (
                <div className="relative group rounded-xl overflow-hidden border border-purple-200 bg-white max-h-64">
                  <img 
                    src={caseAttachmentUrl} 
                    alt="Attached Clinical Case" 
                    className="w-full h-full object-cover max-h-64 cursor-pointer transition-transform duration-300 group-hover:scale-105" 
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(caseAttachmentUrl, '_blank');
                    }}
                  />
                </div>
              ) : (
                <a 
                  href={caseAttachmentUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-purple-700 bg-white px-3 py-2 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
                >
                  <FileText size={16} /> View Case Attachment ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-50 mt-2">
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 font-medium">
          <span className="flex items-center gap-1.5">
            <Eye size={16} className="text-gray-400" /> {views ?? 0} views
          </span>
          <span className="flex items-center gap-1.5">
            <Users size={16} className="text-gray-400" /> {applicants} applicants
          </span>
        </div>

        <button
          onClick={() => onViewApplicants && onViewApplicants(opportunity)}
          className="px-5 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
        >
          View Applicants
        </button>
      </div>
    </div>
  );
};

export default MyPostingCard;
